import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import type { PropsWithChildren, ReactNode } from 'react';

interface CollectionAssetToolbarProps {
  /**
   * Additional CSS classes to apply to the toolbar container
   */
  className?: string;
}

/**
 * Toolbar row above the collection asset grid. Lays out two groups: `CollectionAssetToolbar.Filters`
 * flush left and `CollectionAssetToolbar.Controls` flush right. Every control inside is purely
 * controlled, so all state lives with the caller and selections can be persisted in the URL.
 *
 * @param props - The component props
 */
function CollectionAssetToolbar({ className, children }: PropsWithChildren<CollectionAssetToolbarProps>) {
  return <div className={cx('flex w-full items-center justify-between', className)}>{children}</div>;
}

interface CollectionAssetToolbarGroupProps {
  /**
   * Additional CSS classes to apply to the group
   */
  className?: string;
}

/**
 * The left group of the toolbar: filters that narrow which assets are shown.
 *
 * @param props - The component props
 */
function CollectionAssetToolbarFilters({ className, children }: PropsWithChildren<CollectionAssetToolbarGroupProps>) {
  return <div className={cx('flex items-center gap-4', className)}>{children}</div>;
}

/**
 * The right group of the toolbar: labelled view controls such as the sort order, spaced apart more widely than a
 * control sits from its own label so each pair reads as one.
 *
 * @param props - The component props
 */
function CollectionAssetToolbarControls({ className, children }: PropsWithChildren<CollectionAssetToolbarGroupProps>) {
  return <div className={cx('flex shrink-0 items-center gap-6', className)}>{children}</div>;
}

interface CollectionAssetToolbarControlProps {
  /**
   * Label rendered before the control, e.g. "Sort by:"
   */
  label: ReactNode;
}

/**
 * A labelled slot inside `CollectionAssetToolbar.Controls`: the label text followed by the control itself, kept
 * together as one pair.
 *
 * @param props - The component props
 */
function CollectionAssetToolbarControl({ label, children }: PropsWithChildren<CollectionAssetToolbarControlProps>) {
  return (
    <div className="flex items-center gap-4">
      <Text size={TextSize.Sm} weight={TextWeight.Semibold} color={TextColor.Tertiary}>
        {label}
      </Text>
      {children}
    </div>
  );
}

export { CollectionAssetToolbar, CollectionAssetToolbarFilters, CollectionAssetToolbarControls };
export { CollectionAssetToolbarControl };
export type { CollectionAssetToolbarProps, CollectionAssetToolbarGroupProps, CollectionAssetToolbarControlProps };
